// @ts-check
import React from 'react';

import Button, { buttonClasses } from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Box from '@liquidweb-dev/undercurrent/Box';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import BadgePercent from '@liquidweb-dev/undercurrent/icons/lucide/BadgePercent';
import CircleFadingPlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleFadingPlus';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import CardLayeredHeader from 'components/CardLayeredHeader';
import ProductBrandBlock from 'components/ProductBrandBlock';

/**
 * @typedef AvailableAddOnCardProps
 * @property {string} code The code of the add-on
 * @property {string} title The title of the add-on
 * @property {string} description Short description of the add-on
 * @property {string} price Formatted price string
 * @property {() => void} onAddClick Callback when add button is clicked
 * @property {boolean} [isLoading]
 * @property {string} [discount] Formatted discount string
 * @property {string} [buttonText] Text for the add button
 * @property {boolean} [disabled]
 * @property {string} [tooltipMessage] Message to display in tooltip when disabled
 */

/** @param {Omit<AvailableAddOnCardProps, 'tooltipMessage'> & import('@liquidweb-dev/undercurrent/Stack').StackProps} props */
const Content = ({
	code,
	title,
	description,
	price,
	onAddClick,
	isLoading,
	discount,
	buttonText = 'Add to server',
	disabled,
	sx,
	...stackProps
}) => {
	if (isLoading) {
		return (
			<Stack sx={sx} {...stackProps}>
				<Card sx={{ flexGrow: 1 }}>
					<CardContent>
						<Skeleton variant="rounded" height={260} width="100%" />
					</CardContent>
				</Card>
			</Stack>
		);
	}

	return (
		<Stack sx={combineSx({ opacity: disabled ? 0.5 : 1 }, sx)} {...stackProps}>
			{discount && (
				<CardLayeredHeader
					sx={({ vars }) => ({
						display: 'flex',
						alignItems: 'center',
						gap: 100,
						backgroundColor: vars.palette.uc.success.bg.main,
						color: vars.palette.uc.success.text.main,
					})}
				>
					<BadgePercent />
					<Typography
						variant="label-lg"
						sx={{
							fontWeight: 500,
						}}
					>
						Get {discount}
					</Typography>
				</CardLayeredHeader>
			)}

			<Card
				sx={{
					flexGrow: 1,
					minHeight: '360px',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<CardContent
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 200,
						justifyContent: 'space-between',
						height: '100%',
						flexGrow: 1,
					}}
				>
					<Stack
						sx={{
							flexDirection: 'column',
							gap: 200,
							justifyContent: 'space-between',
						}}
					>
						<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
							{title}
						</Typography>
						<ProductBrandBlock
							code={code}
							sx={{ display: 'block', width: '100%' }}
						/>
						<Typography
							variant="body-sm"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							{description}
						</Typography>

						<Typography variant="body-md" sx={{ fontWeight: 500 }}>
							{price}
						</Typography>
					</Stack>

					<Button
						onClick={onAddClick}
						disabled={disabled}
						startIcon={disabled ? undefined : <CircleFadingPlusIcon />}
						variant={disabled ? 'secondary' : 'primary'}
						color="highlight"
						sx={{
							[`&.${buttonClasses.disabled}`]: {
								backgroundColor: ({ vars }) =>
									vars.palette.uc.highlight.bg.weak,
							},
						}}
						size="large"
					>
						{buttonText}
					</Button>
				</CardContent>
			</Card>
		</Stack>
	);
};

/** @param {AvailableAddOnCardProps & import('@liquidweb-dev/undercurrent/Stack').StackProps} props */
const AvailableAddOnCard = ({ disabled, tooltipMessage, ...rest }) => {
	if (disabled) {
		return (
			<Tooltip
				arrow
				variant="fancy"
				placement="top"
				title="What's the problem?"
				message={tooltipMessage}
			>
				<Box>
					<Content disabled={disabled} {...rest} />
				</Box>
			</Tooltip>
		);
	}
	return <Content {...rest} />;
};

export default AvailableAddOnCard;
